/**
 * Fighter Trivia Popup
 * Interactive popup where users answer a trivia question to unlock a discount
 */

class FighterTriviaPopup {
    constructor() {
      this.popup = document.getElementById('FighterTriviaPopup');
      if (!this.popup) return;
  
      this.overlay = this.popup.querySelector('.fighter-trivia-popup__overlay');
      this.closeBtn = this.popup.querySelector('.fighter-trivia-popup__close');
      this.screens = this.popup.querySelectorAll('.fighter-trivia-popup__screen');
      this.fighters = this.popup.querySelectorAll('.fighter-trivia-popup__fighter');
      this.form = document.getElementById('FighterTriviaForm');
      this.closeBtns = this.popup.querySelectorAll('[data-action="close"]');
  
      this.popupDelay = parseInt(this.popup.dataset.popupDelay || '3', 10) * 1000;
      this.correctAnswer = this.popup.dataset.correctAnswer || '';
      this.klaviyoListId = this.popup.dataset.klaviyoList || '';
  
      this.storageKey = 'fighterTriviaPopup';
      this.lockoutDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      this.maxAttempts = 4; // Number of chances before lockout
      this.currentAttempts = 0;
  
      this.#init();
    }
  
    #init() {
      // Check if user should see the popup
      if (!this.#shouldShowPopup()) return;
  
      // Set up event listeners
      this.#bindEvents();
  
      // Show popup after delay
      setTimeout(() => this.#openPopup(), this.popupDelay);
    }
  
    #bindEvents() {
      // Close button
      this.closeBtn.addEventListener('click', () => this.#closePopup());
      
      // Overlay click
      this.overlay.addEventListener('click', () => this.#closePopup());
  
      // Close buttons within screens
      for (const btn of this.closeBtns) {
        btn.addEventListener('click', () => this.#closePopup());
      }
  
      // Fighter clicks
      for (const fighter of this.fighters) {
        fighter.addEventListener('click', (e) => this.#handleFighterClick(e));
      }
  
      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.#handleFormSubmit(e));
      }
  
      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.popup.getAttribute('aria-hidden') === 'false') {
          this.#closePopup();
        }
      });
    }
  
    #shouldShowPopup() {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return true;
  
      try {
        const data = JSON.parse(stored);
        
        // User already completed the flow
        if (data.completed) return false;
  
        // User dismissed the popup - don't show again
        if (data.dismissed) return false;
  
        // User used all attempts - check lockout
        if (data.lockedOut) {
          const lockoutEnd = data.lockoutTime + this.lockoutDuration;
          if (Date.now() < lockoutEnd) {
            return false; // Still locked out
          }
          // Lockout expired, reset attempts
          localStorage.removeItem(this.storageKey);
          return true;
        }
  
        // User has attempts remaining
        if (data.attempts !== undefined) {
          this.currentAttempts = data.attempts;
          return true; // Let them continue trying
        }
  
        // User got correct answer but hasn't finished email signup
        if (data.correctAnswer) {
          return true; // Let them continue
        }
  
        return true;
      } catch (e) {
        return true;
      }
    }
  
    #openPopup() {
      this.popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Check if user previously got correct answer
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          if (data.correctAnswer && !data.completed) {
            // Resume at email signup
            this.#showScreen('winner');
          }
        } catch (e) {
          // Continue with trivia
        }
      }
    }
  
    #closePopup() {
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Save dismissed state so popup doesn't show again
      const stored = localStorage.getItem(this.storageKey);
      let data = {};
      
      if (stored) {
        try {
          data = JSON.parse(stored);
        } catch (e) {
          data = {};
        }
      }
      
      // Only mark as dismissed if user hasn't completed or isn't in progress
      if (!data.completed && !data.correctAnswer) {
        data.dismissed = true;
        data.dismissedTime = Date.now();
        localStorage.setItem(this.storageKey, JSON.stringify(data));
      }
    }
  
    #showScreen(screenName) {
      for (const screen of this.screens) {
        screen.classList.remove('active');
        if (screen.dataset.screen === screenName) {
          screen.classList.add('active');
        }
      }
    }
  
    #handleFighterClick(e) {
      const fighter = e.currentTarget;
      const fighterId = fighter.dataset.fighter;
  
      if (fighterId === this.correctAnswer) {
        this.#handleCorrectAnswer(fighter);
      } else {
        this.#handleWrongAnswer(fighter);
      }
    }
  
    #handleCorrectAnswer(clickedFighter) {
      // Add visual feedback
      this.#addClickFeedback(true, clickedFighter);
      
      // Save state
      localStorage.setItem(this.storageKey, JSON.stringify({
        correctAnswer: true,
        correctAnswerTime: Date.now()
      }));
  
      // Show winner screen after brief animation
      setTimeout(() => {
        this.#showScreen('winner');
      }, 500);
    }
  
    #handleWrongAnswer(clickedFighter) {
      // Add visual feedback
      this.#addClickFeedback(false, clickedFighter);
      
      // Increment attempts
      this.currentAttempts++;
      const remainingAttempts = this.maxAttempts - this.currentAttempts;
      
      // Remove/fade out the wrong fighter
      this.#removeFighter(clickedFighter);
      
      // Check if all attempts used
      if (remainingAttempts <= 0) {
        // Lock out user
        localStorage.setItem(this.storageKey, JSON.stringify({
          lockedOut: true,
          lockoutTime: Date.now(),
          attempts: this.currentAttempts
        }));
  
        // Update wrong screen text for lockout
        this.#updateWrongScreenText(0, true);
  
        // Show wrong screen after brief delay
        setTimeout(() => {
          this.#showScreen('wrong');
          
          // Auto close after 3 seconds
          setTimeout(() => {
            this.#closePopup();
          }, 3000);
        }, 800);
      } else {
        // Save attempts, allow retry
        localStorage.setItem(this.storageKey, JSON.stringify({
          attempts: this.currentAttempts
        }));
  
        // Show inline toast message - stay on trivia screen
        this.#showToast(remainingAttempts);
      }
    }
  
    #removeFighter(fighter) {
      if (!fighter) return;
      
      // Add removal animation class
      fighter.classList.add('fighter-trivia-popup__fighter--removed');
      
      // Disable the fighter button
      fighter.disabled = true;
      fighter.style.pointerEvents = 'none';
    }
  
    #showToast(remaining) {
      // Create or update toast element
      let toast = this.popup.querySelector('.fighter-trivia-popup__toast');
      
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'fighter-trivia-popup__toast';
        const triviaScreen = this.popup.querySelector('.fighter-trivia-popup__screen--trivia');
        if (triviaScreen) {
          triviaScreen.appendChild(toast);
        }
      }
      
      // Set message based on remaining attempts
      let message = '';
      if (remaining === 1) {
        message = '❌ Wrong! Last chance!';
      } else {
        message = `❌ Wrong! ${remaining} tries left`;
      }
      
      toast.textContent = message;
      toast.classList.add('fighter-trivia-popup__toast--visible');
      
      // Hide toast after 2 seconds
      setTimeout(() => {
        toast.classList.remove('fighter-trivia-popup__toast--visible');
      }, 2000);
    }
  
    #updateWrongScreenText(remaining, isLockedOut) {
      const wrongText = this.popup.querySelector('.fighter-trivia-popup__wrong-text');
      const wrongTimer = this.popup.querySelector('.fighter-trivia-popup__wrong-timer');
      
      if (wrongText) {
        if (isLockedOut) {
          wrongText.textContent = 'No more chances left!';
        } else if (remaining === 1) {
          wrongText.textContent = 'Last chance remaining!';
        } else {
          wrongText.textContent = `${remaining} chances remaining. Try again!`;
        }
      }
      
      if (wrongTimer) {
        if (isLockedOut) {
          wrongTimer.style.display = 'flex';
        } else {
          wrongTimer.style.display = 'none';
        }
      }
    }
  
    #addClickFeedback(isCorrect, fighter) {
      if (!fighter) return;
  
      fighter.style.borderColor = isCorrect ? 'var(--ftp-success)' : 'var(--ftp-error)';
      fighter.style.boxShadow = isCorrect 
        ? '0 0 30px rgba(52, 199, 89, 0.5)' 
        : '0 0 30px rgba(255, 59, 48, 0.5)';
      
      // Reset styles after animation
      setTimeout(() => {
        fighter.style.borderColor = '';
        fighter.style.boxShadow = '';
      }, 2000);
    }
  
    async #handleFormSubmit(e) {
      e.preventDefault();
      
      const emailInput = this.form.querySelector('input[type="email"]');
      const email = emailInput.value.trim();
      
      if (!email) return;
  
      const submitBtn = this.form.querySelector('button[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';
  
      // Submit to Klaviyo
      const success = await this.#submitToKlaviyo(email);
      
      if (success) {
        // Mark as completed
        localStorage.setItem(this.storageKey, JSON.stringify({
          completed: true,
          email: email,
          completedTime: Date.now()
        }));
  
        // Show discount screen
        this.#showScreen('discount');
      } else {
        // Still show discount (fail gracefully)
        localStorage.setItem(this.storageKey, JSON.stringify({
          completed: true,
          email: email,
          completedTime: Date.now()
        }));
        this.#showScreen('discount');
      }
    }
  
    async #submitToKlaviyo(email) {
      if (!this.klaviyoListId) {
        console.warn('Klaviyo list ID not configured');
        return false;
      }

      // Get Klaviyo company ID from the loaded script
      const companyId = this.#getKlaviyoCompanyId();
      
      // Method 1: Use Klaviyo's modern Client API (2024+)
      // This is the most reliable method for subscribing to a list
      if (companyId) {
        try {
          const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2024-02-15'
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        properties: {
                          'Fighter Trivia Winner': true,
                          'Fighter Trivia Date': new Date().toISOString()
                        }
                      }
                    }
                  },
                  custom_source: 'Fighter Trivia Popup'
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: this.klaviyoListId
                    }
                  }
                }
              }
            })
          });

          if (response.ok || response.status === 202) {
            console.log('Klaviyo subscription successful via Client API');
            return true;
          } else {
            const errorText = await response.text();
            console.error('Klaviyo Client API error:', response.status, errorText);
          }
        } catch (e) {
          console.error('Klaviyo Client API error:', e);
        }
      }

      // Method 2: Use Klaviyo's _learnq object (legacy but widely used)
      if (typeof window._learnq !== 'undefined') {
        try {
          window._learnq.push(['identify', {
            '$email': email,
            'Fighter Trivia Winner': true,
            'Fighter Trivia Date': new Date().toISOString()
          }]);

          console.log('Klaviyo identification successful via _learnq');
          // Note: This identifies the user but doesn't add to a list directly
          // The Client API above should handle list subscription
          return true;
        } catch (e) {
          console.error('Klaviyo _learnq error:', e);
        }
      }

      // Method 3: Use Klaviyo's modern klaviyo object (if available)
      if (typeof window.klaviyo !== 'undefined' && window.klaviyo.identify) {
        try {
          window.klaviyo.identify({
            '$email': email,
            'Fighter Trivia Winner': true,
            'Fighter Trivia Date': new Date().toISOString()
          });

          console.log('Klaviyo identification successful via klaviyo.identify');
          return true;
        } catch (e) {
          console.error('Klaviyo identify error:', e);
        }
      }

      // Method 4: Fallback - use _klOnsite if available
      if (typeof window._klOnsite !== 'undefined') {
        try {
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(['identify', {
            '$email': email,
            'Fighter Trivia Winner': true,
            'Fighter Trivia Date': new Date().toISOString()
          }]);

          console.log('Klaviyo onsite identify successful');
          return true;
        } catch (e) {
          console.error('Klaviyo onsite error:', e);
        }
      }

      console.warn('No Klaviyo integration method succeeded');
      return false;
    }

    #getKlaviyoCompanyId() {
      // Try to get the company ID from the loaded Klaviyo script
      const scripts = document.querySelectorAll('script[src*="klaviyo"]');
      for (const script of scripts) {
        const match = script.src.match(/company_id=([A-Za-z0-9]+)/);
        if (match) {
          return match[1];
        }
      }
      
      // Fallback: Check if window.klaviyo has the company ID
      if (typeof window.klaviyo !== 'undefined' && window.klaviyo.account) {
        return window.klaviyo.account;
      }

      // Hardcoded fallback for prolast.com - company ID from network inspection
      return 'YyL3Am';
    }
  
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new FighterTriviaPopup());
  } else {
    new FighterTriviaPopup();
  }
  
  // Export for potential external use
  window.FighterTriviaPopup = FighterTriviaPopup;
  